import * as THREE from "three";
import {
  HEALTHY_ROOM_CONNECTIONS,
  isRoomOutputId,
  isRoomPanelNodeId,
  ROOM_OUTPUT_IDS,
  ROOM_SOURCE_BUS_ID,
  ROOM_SOURCE_BUS_MAX_OCCUPANTS,
  type RoomCircuitState,
  type RoomOutputId,
} from "../electrical";
import type { ControllerHandle, InteractionSystem } from "../xr/input";
import type { XRRig } from "../xr/session";
import type { WorkshopEnvironment } from "../world/room";
import { roomChallenge } from "../workshop/RoomChallenge";
import { JumperSystem, type JumperInitialSeat } from "./JumperSystem";
import { MeterSystem } from "./MeterSystem";

/** Smooth follow for fixture emissive / fill light (higher = snappier). */
const VISUAL_FOLLOW = 8;
const FAN_MAX_SPEED = 9;
const FAN_ACCEL = 14;
const FILL_INTENSITY_PER_CEILING = 0.38;

const SWITCH_OFF_Y = -0.012;
const SWITCH_ON_Y = 0.012;
const SWITCH_OFF_ROT_X = 0.38;
const SWITCH_ON_ROT_X = -0.38;

const LIGHT_ON_EMISSIVE = 0xf0d890;
const LIGHT_OFF_EMISSIVE = 0x334044;
const LIGHT_ON_INTENSITY = 0.55;
const LIGHT_OFF_INTENSITY = 0.04;
const WALL_ON_INTENSITY = 0.32;
const WALL_OFF_INTENSITY = 0.03;
const SIGN_ON_INTENSITY = 0.48;
const SIGN_OFF_INTENSITY = 0.04;
const INDICATOR_ON = 0.45;
const INDICATOR_OFF = 0.06;
const OUTLET_ON = 0.4;
const OUTLET_OFF = 0.05;

const OUTPUT_DEVICE = {
  outLightA: "ceilingLightA",
  outLightB: "ceilingLightB",
  outSign: "sign",
  outFan: "fan",
  outOutlet: "outlet",
} as const satisfies Record<RoomOutputId, string>;

export interface RoomPanelSystem {
  readonly jumperSystem: JumperSystem;
  readonly meterSystem: MeterSystem;
  update(dt: number): void;
  dispose(): void;
}

export interface CreateRoomPanelSystemOptions {
  rig: XRRig;
  interaction: InteractionSystem;
  env: WorkshopEnvironment;
}

/**
 * Panel jumpers, LIGHT B wall switch, portable meter, and device response
 * for “find the fault.”
 *
 * Does not register as a WorkshopStation — the panel control labeled NEW FAULT
 * calls {@link roomChallenge.newFault} (outlet trainer and player pose stay
 * put). {@link roomChallenge.reset} re-applies the same scenario (KeyR).
 * Session holds drop via {@link roomChallenge.registerSessionHoldReleaser}.
 * Meter restore-to-dock happens on newFault/reset, not on session drop.
 */
export function createRoomPanelSystem(
  opts: CreateRoomPanelSystemOptions,
): RoomPanelSystem {
  const { rig, interaction, env } = opts;

  const jumpers = new JumperSystem({
    rig,
    interaction,
    layout: {
      restRoot: env.restPosts,
      terminals: env.terminals,
      jumpers: env.jumpers,
      terminalMaxOccupants: (nodeId) =>
        nodeId === ROOM_SOURCE_BUS_ID ? ROOM_SOURCE_BUS_MAX_OCCUPANTS : 1,
      isCircuitNodeId: isRoomPanelNodeId,
      initialSeating: seatingFromConnections(
        roomChallenge.getConnections(),
        env.jumpers,
      ),
    },
    onConnectionsChanged: () => {
      roomChallenge.setConnections(jumpers.collectConnections());
    },
  });
  roomChallenge.setConnections(jumpers.collectConnections());

  const meter = new MeterSystem({ rig, interaction, env });

  const toggle = env.lightBSwitch.getObjectByName("switch-light-b-toggle");
  const outletIndicator = env.outletA.getObjectByName("outlet-a-indicator");
  const outletMat =
    outletIndicator instanceof THREE.Mesh &&
    outletIndicator.material instanceof THREE.MeshStandardMaterial
      ? outletIndicator.material
      : null;

  const fill = new THREE.PointLight(0xfff0c8, 0, 14, 2);
  fill.name = "room-ceiling-fill";
  fill.castShadow = false;
  fill.position.set(-0.6, 2.55, -0.2);
  env.room.add(fill);

  const progress = bindProgressPlate(env.progressIndicator);

  let introVisible = false;
  env.introPanel.visible = false;

  let targetLightA = LIGHT_OFF_INTENSITY;
  let targetLightB = LIGHT_OFF_INTENSITY;
  let targetWall = WALL_OFF_INTENSITY;
  let targetSign = SIGN_OFF_INTENSITY;
  let targetFill = 0;
  let displayLightA = env.ceilingLightAMaterial.emissiveIntensity;
  let displayLightB = env.ceilingLightBMaterial.emissiveIntensity;
  let displayWall = env.wallLightMaterial.emissiveIntensity;
  let displaySign = env.signMaterial.emissiveIntensity;
  let displayFill = 0;
  const targetIndicators: Record<RoomOutputId, number> = {
    outLightA: INDICATOR_OFF,
    outLightB: INDICATOR_OFF,
    outSign: INDICATOR_OFF,
    outFan: INDICATOR_OFF,
    outOutlet: INDICATOR_OFF,
  };
  const displayIndicators: Record<RoomOutputId, number> = {
    outLightA: env.indicators.outLightA.material.emissiveIntensity,
    outLightB: env.indicators.outLightB.material.emissiveIntensity,
    outSign: env.indicators.outSign.material.emissiveIntensity,
    outFan: env.indicators.outFan.material.emissiveIntensity,
    outOutlet: env.indicators.outOutlet.material.emissiveIntensity,
  };
  let targetOutlet = OUTLET_OFF;
  let displayOutlet = outletMat?.emissiveIntensity ?? OUTLET_OFF;
  let fanPowered = false;
  let fanSpeed = 0;

  const applyState = (state: RoomCircuitState, immediate: boolean): void => {
    const aOn = state.isPowered("ceilingLightA");
    const bOn = state.isPowered("ceilingLightB");
    const signOn = state.isPowered("sign");
    fanPowered = state.isPowered("fan");
    const outletOn = state.isPowered("outlet");
    const ceilingCount = (aOn ? 1 : 0) + (bOn ? 1 : 0);

    targetLightA = aOn ? LIGHT_ON_INTENSITY : LIGHT_OFF_INTENSITY;
    targetLightB = bOn ? LIGHT_ON_INTENSITY : LIGHT_OFF_INTENSITY;
    // Wall sconce follows Light A (not a separate circuit device).
    targetWall = aOn ? WALL_ON_INTENSITY : WALL_OFF_INTENSITY;
    targetSign = signOn ? SIGN_ON_INTENSITY : SIGN_OFF_INTENSITY;
    targetFill = ceilingCount * FILL_INTENSITY_PER_CEILING;
    targetOutlet = outletOn ? OUTLET_ON : OUTLET_OFF;

    setEmissiveColor(env.ceilingLightAMaterial, aOn);
    setEmissiveColor(env.ceilingLightBMaterial, bOn);
    setEmissiveColor(env.wallLightMaterial, aOn);
    env.signMaterial.emissive.setHex(signOn ? 0xa8cce8 : 0x334455);

    for (const outputId of ROOM_OUTPUT_IDS) {
      const on = state.isPowered(OUTPUT_DEVICE[outputId]);
      targetIndicators[outputId] = on ? INDICATOR_ON : INDICATOR_OFF;
      const ind = env.indicators[outputId];
      ind.material.color.setHex(on ? 0xc8e8a8 : 0x3a4438);
      ind.material.emissive.setHex(on ? 0x88cc55 : 0x223318);
    }
    if (outletMat) {
      outletMat.color.setHex(outletOn ? 0xc8e8a8 : 0x3a4438);
      outletMat.emissive.setHex(outletOn ? 0x88cc55 : 0x223318);
    }

    // Paddle follows the switch, not the gated lamp (ON + missing jumper
    // still shows ON; Light B stays off until jumpered).
    applySwitchVisual(toggle, roomChallenge.getSwitches().switchLightB === true);

    paintProgress(progress, state.poweredCount, state.totalCount);

    if (immediate) {
      displayLightA = targetLightA;
      displayLightB = targetLightB;
      displayWall = targetWall;
      displaySign = targetSign;
      displayFill = targetFill;
      displayOutlet = targetOutlet;
      for (const outputId of ROOM_OUTPUT_IDS) {
        displayIndicators[outputId] = targetIndicators[outputId];
      }
      commitVisuals();
      if (!fanPowered) fanSpeed = 0;
    }
  };

  const commitVisuals = (): void => {
    env.ceilingLightAMaterial.emissiveIntensity = displayLightA;
    env.ceilingLightBMaterial.emissiveIntensity = displayLightB;
    env.wallLightMaterial.emissiveIntensity = displayWall;
    env.signMaterial.emissiveIntensity = displaySign;
    fill.intensity = displayFill;
    if (outletMat) outletMat.emissiveIntensity = displayOutlet;
    for (const outputId of ROOM_OUTPUT_IDS) {
      env.indicators[outputId].material.emissiveIntensity =
        displayIndicators[outputId];
    }
  };

  applyState(roomChallenge.getState(), true);

  const hint = bindHintPanel(env);
  let hintVisible = false;
  const setHintVisible = (visible: boolean): void => {
    hintVisible = visible;
    hint.mesh.visible = visible;
    if (visible) {
      paintHint(
        hint,
        roomChallenge.getActiveFaultHint() ??
          "Check whether power reaches the affected device.",
      );
    }
  };
  if (env.hintButton) {
    env.hintButton.visible = true;
    interaction.register({
      object: env.hintButton,
      onSelect: () => setHintVisible(!hintVisible),
    });
  }

  const unsubState = roomChallenge.registerOnStateChange((state) => {
    applyState(state, false);
  });

  const unsubReset = roomChallenge.registerOnReset(() => {
    jumpers.restoreSeating(
      seatingFromConnections(roomChallenge.getConnections(), env.jumpers),
    );
    meter.restoreToRest();
    introVisible = false;
    env.introPanel.visible = false;
    setHintVisible(false);
    applySwitchVisual(toggle, roomChallenge.getSwitches().switchLightB === true);
  });

  const unsubHold = roomChallenge.registerSessionHoldReleaser(() => {
    jumpers.releaseSessionHolds();
    meter.releaseSessionHolds();
  });

  const setIntroVisible = (visible: boolean): void => {
    introVisible = visible;
    env.introPanel.visible = visible;
  };

  interaction.register({
    object: env.introButton,
    onSelect: () => setIntroVisible(!introVisible),
  });

  interaction.register({
    object: env.reset,
    onSelect: () => roomChallenge.newFault(),
  });

  const toggleLightB = (): void => {
    const next = roomChallenge.getSwitches().switchLightB !== true;
    roomChallenge.setSwitch("switchLightB", next);
    applySwitchVisual(toggle, next);
  };

  interaction.register({
    object: env.lightBSwitch,
    onSelect: () => toggleLightB(),
  });

  const raycaster = new THREE.Raycaster();
  const _origin = new THREE.Vector3();
  const _dir = new THREE.Vector3();
  const _quat = new THREE.Quaternion();

  const pointingAtSwitch = (controller: ControllerHandle): boolean => {
    _origin.set(0, 0, 0);
    _dir.set(0, 0, -1);
    controller.targetRay.getWorldPosition(_origin);
    controller.targetRay.getWorldQuaternion(_quat);
    _dir.applyQuaternion(_quat);
    raycaster.set(_origin, _dir);
    raycaster.far = 1.6;
    const hits = raycaster.intersectObject(env.lightBSwitch, true);
    return hits.length > 0;
  };

  const unbindListener = interaction.addListener({
    onSqueezeStart: (controller) => {
      if (controller.owned) return;
      if (pointingAtSwitch(controller)) toggleLightB();
    },
  });

  return {
    jumperSystem: jumpers,
    meterSystem: meter,
    update(dt: number) {
      jumpers.update(dt);
      meter.update(dt);
      const k = 1 - Math.exp(-VISUAL_FOLLOW * dt);
      displayLightA += (targetLightA - displayLightA) * k;
      displayLightB += (targetLightB - displayLightB) * k;
      displayWall += (targetWall - displayWall) * k;
      displaySign += (targetSign - displaySign) * k;
      displayFill += (targetFill - displayFill) * k;
      displayOutlet += (targetOutlet - displayOutlet) * k;
      for (const outputId of ROOM_OUTPUT_IDS) {
        displayIndicators[outputId] +=
          (targetIndicators[outputId] - displayIndicators[outputId]) * k;
      }
      commitVisuals();

      if (fanPowered) {
        fanSpeed = Math.min(FAN_MAX_SPEED, fanSpeed + FAN_ACCEL * dt);
      } else {
        fanSpeed = Math.max(0, fanSpeed - FAN_ACCEL * dt);
      }
      if (fanSpeed > 0) {
        env.fanBlades.rotation.z += fanSpeed * dt;
      }
    },
    dispose() {
      unsubState();
      unsubReset();
      unsubHold();
      unbindListener();
      jumpers.dispose();
      meter.dispose();
      fill.removeFromParent();
      progress.texture.dispose();
      hint.texture.dispose();
      hint.mesh.removeFromParent();
      interaction.unregister(env.introButton);
      interaction.unregister(env.reset);
      interaction.unregister(env.lightBSwitch);
      if (env.hintButton) interaction.unregister(env.hintButton);
    },
  };
}

/**
 * Seat one jumper per healthy srcP↔output pair that is still present.
 * Missing pairs (the authored open) stay on rest posts. Uses HEALTHY order
 * so jumper-i tracks ROOM_OUTPUT_IDS[i].
 */
function seatingFromConnections(
  connections: ReadonlyArray<readonly [string, string]>,
  jumpers: readonly { id: string }[],
): JumperInitialSeat[] {
  const connectedOutputs = new Set<string>();
  for (const pair of connections) {
    const a = pair[0];
    const b = pair[1];
    if (a === ROOM_SOURCE_BUS_ID && isRoomOutputId(b)) connectedOutputs.add(b);
    else if (b === ROOM_SOURCE_BUS_ID && isRoomOutputId(a)) connectedOutputs.add(a);
  }

  const seats: JumperInitialSeat[] = [];
  for (let i = 0; i < HEALTHY_ROOM_CONNECTIONS.length && i < jumpers.length; i++) {
    const outputId = HEALTHY_ROOM_CONNECTIONS[i]![1];
    if (!connectedOutputs.has(outputId)) continue;
    const jumper = jumpers[i]!;
    seats.push({ jumperId: jumper.id, end: "a", nodeId: ROOM_SOURCE_BUS_ID });
    seats.push({ jumperId: jumper.id, end: "b", nodeId: outputId });
  }
  return seats;
}

function setEmissiveColor(mat: THREE.MeshStandardMaterial, on: boolean): void {
  mat.emissive.setHex(on ? LIGHT_ON_EMISSIVE : LIGHT_OFF_EMISSIVE);
}

function applySwitchVisual(toggle: THREE.Object3D | undefined, closed: boolean): void {
  if (!toggle) return;
  toggle.position.y = closed ? SWITCH_ON_Y : SWITCH_OFF_Y;
  toggle.rotation.x = closed ? SWITCH_ON_ROT_X : SWITCH_OFF_ROT_X;
  toggle.userData.state = closed ? "on" : "off";
  if (toggle.parent) {
    toggle.parent.userData.state = closed ? "on" : "off";
  }
}

interface ProgressPlate {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  texture: THREE.CanvasTexture;
  lastKey: string;
}

function bindProgressPlate(mesh: THREE.Mesh): ProgressPlate {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 2;

  const mat = mesh.material;
  if (mat instanceof THREE.MeshBasicMaterial) {
    mat.map?.dispose();
    mat.map = texture;
    mat.needsUpdate = true;
  } else {
    mesh.material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
    });
  }

  return { canvas, ctx, texture, lastKey: "" };
}

function paintProgress(
  plate: ProgressPlate,
  poweredCount: number,
  totalCount: number,
): void {
  const complete = totalCount > 0 && poweredCount >= totalCount;
  const key = `${poweredCount}/${totalCount}:${complete ? "cleared" : "run"}`;
  if (key === plate.lastKey) return;
  plate.lastKey = key;

  const { ctx, canvas, texture } = plate;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "rgba(18, 28, 26, 0.92)";
  roundRect(ctx, 8, 10, canvas.width - 16, canvas.height - 20, 12);
  ctx.fill();
  ctx.fillStyle = complete ? "#c8e8a8" : "#d8e6df";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  if (complete) {
    ctx.font = "700 32px system-ui, sans-serif";
    ctx.fillText("Fault cleared.", canvas.width / 2, canvas.height / 2 - 10);
    ctx.font = "600 22px system-ui, sans-serif";
    ctx.fillStyle = "#a8c8b0";
    ctx.fillText("Workshop restored.", canvas.width / 2, canvas.height / 2 + 22);
  } else {
    ctx.font = "700 34px system-ui, sans-serif";
    ctx.fillText("Find the fault.", canvas.width / 2, canvas.height / 2 - 12);
    ctx.font = "600 22px system-ui, sans-serif";
    ctx.fillStyle = "#8aa090";
    ctx.fillText(`${poweredCount}/${totalCount}`, canvas.width / 2, canvas.height / 2 + 22);
  }
  texture.needsUpdate = true;
}

interface HintPlate {
  mesh: THREE.Mesh;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  texture: THREE.CanvasTexture;
  lastKey: string;
}

function bindHintPanel(env: WorkshopEnvironment): HintPlate {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 192;
  const ctx = canvas.getContext("2d")!;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 2;

  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(0.42, 0.16),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
    }),
  );
  mesh.name = "hint-panel";
  mesh.visible = false;
  mesh.position.set(-0.48, 0.28, 0.14);
  env.panel.add(mesh);

  return { mesh, canvas, ctx, texture, lastKey: "" };
}

function paintHint(plate: HintPlate, text: string): void {
  if (text === plate.lastKey) return;
  plate.lastKey = text;

  const { ctx, canvas, texture } = plate;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "rgba(18, 28, 26, 0.92)";
  roundRect(ctx, 8, 10, canvas.width - 16, canvas.height - 20, 12);
  ctx.fill();
  ctx.fillStyle = "#d8e6df";
  ctx.font = "600 28px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const lines = wrapHintLines(ctx, text, canvas.width - 48);
  const lineH = 34;
  const startY = canvas.height / 2 - ((lines.length - 1) * lineH) / 2;
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i]!, canvas.width / 2, startY + i * lineH);
  }
  texture.needsUpdate = true;
}

function wrapHintLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(/\s+/).filter((w) => w.length > 0);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (current && ctx.measureText(next).width > maxWidth) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines.slice(0, 4) : [text];
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}
